import { Pagination } from "@mui/material";
import { CalendarIcon, Cross2Icon } from "@radix-ui/react-icons";
import { Check, EyeIcon, Loader2, Repeat, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import toast from "react-hot-toast";
import { Loader } from "../../components/Widget/Loader";
import { find, store } from "../../services/service";
import {
  CHANGER_STATUT_ABONNEMENT_RETOURNEE,
  RECHERCHER_ABONNEMENT_RETOURNEE,
} from "../../Utils/constant";

export const AbonnementRetourne = () => {
  const [ventes, setVentes] = useState({ data: [], totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [size] = useState(10);
  const [param, setParam] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [selectedIds, setSelectedIds] = useState([]);
  const [statusIntention, setStatusIntention] = useState("");
  // Gestion de la sélection d'une vente
  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  // Sélectionner tout
  const handleSelectAll = () => {
    if (
      ventes?.data?.filter((e) => e.statut === "EN_ATTENTE_DE_TRAITEMENT")
        ?.length === selectedIds?.length
    ) {
      setSelectedIds([]);
    } else {
      setSelectedIds(
        ventes?.data
          ?.filter((e) => e.statut === "EN_ATTENTE_DE_TRAITEMENT")
          .map((d) => d.id),
      );
    }
  };

  // Suppression par lot avec loading
  const [batchLoading, setBatchLoading] = useState(null); // null | 'valider' | 'rejeter'
  const handleStatutEdit = async (state) => {
    if (!selectedIds.length) return;
    setBatchLoading(state ? "valider" : "rejeter");

    try {
      await store(CHANGER_STATUT_ABONNEMENT_RETOURNEE, "application/json", {
        ids: selectedIds,
        commentaire: commentaire,
        statut: state,
      });
      toast.success(state ? "Abonnement traité" : "Abonnment rejeté");
      setSelectedIds([]);
      loadVentes(page);
    } catch (err) {
      toast.error(
        err?.response?.data?.donnee || "Erreur lors de la suppression en lot",
      );
    } finally {
      setBatchLoading(null);
    }
  };

  const [actionToConfirm, setActionToConfirm] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);

  const requestValidate = (id) => {
    handleSelect(id);
    document.getElementById("confirm_action").showModal();
  };

  const cancelAction = () => {
    document.getElementById("confirm_action").close();
    setActionToConfirm(null);
  };

  const openDetails = (sale) => {
    setSelectedSale(sale);
    document.getElementById("details_sale").showModal();
  };

  const loadVentes = async (p = 1, debut, fin) => {
    setLoading(true);
    try {
      const q = `?dateDebut=${debut ?? ""}&dateFin=${
        fin ?? ""
      }&param=${param}&page=${p - 1}&size=${size}`;
      const res = await find(`${RECHERCHER_ABONNEMENT_RETOURNEE}${q}`);
      const data = res?.data?.donnee;
      setVentes(data);
      setPage(p);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVentes(1);
  }, [param]);

  return (
    <div className="p-4">
      <div className="mb-6">
        <h2 className="font-mtn text-3xl font-bold mb-2">
          Gestion des abonnements retournés
        </h2>
        <p className="text-sm text-gray-500">
          Gestion des abonnement retournés par les techniciens sur le terrain
        </p>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <input
            value={param}
            onChange={(e) => {
              setParam(e.target.value);
            }}
            type="text"
            placeholder="Rechercher un prestataire par nom..."
            className="input input-bordered px-3 pl-10 pr-10 w-full h-11 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          />
          <Search className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
        </div>
        <div className="px-3 py-2 border border-gray-300 rounded-lg text-sm h-11 flex items-center gap-2 max-w-sm">
          <CalendarIcon className="w-5 h-5 text-gray-400" />
          <ReactDatePicker
            dateFormat="dd/MM/yyyy"
            selected={startDate}
            onChange={(update) => {
              if (update[1] != null) {
                loadVentes(
                  1,
                  new Date(update[0]).toLocaleDateString("es-CL"),
                  new Date(update[1]).toLocaleDateString("es-CL"),
                );
              }
              setDateRange(update);
            }}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            placeholderText="Sélectionner une période"
            className="w-56 h-7 cursor-pointer text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
          />
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          {/* Mobile / small screens: cards */}
          <div className="xl:hidden grid md:grid-cols-2 gap-3">
            {ventes?.data?.length ? (
              ventes.data.map((d) => (
                <div key={d?.id} className="p-4 border rounded bg-white">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="text-sm font-semibold">
                        N° {d?.abonnement?.eligibilite?.numeroTicket || d?.id}
                      </p>
                      <p className="text-xs text-gray-500">
                        {d?.abonnement?.client?.nomPrenoms || "-"}
                      </p>
                      <p className="text-xs text-gray-500">
                        Forfait: {d?.abonnement?.forfait?.designation || "-"}
                      </p>
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(
                        d?.dateCreation || Date.now(),
                      ).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-2">
                    {/* status badge */}
                    <span
                      className={`text-xs inline-block ${
                        d?.statut === "EN_ATTENTE_DE_TRAITEMENT"
                          ? "bg-amber-100 text-amber-700"
                          : d?.statut === "REJETE"
                            ? "bg-red-100 text-red-700"
                            : "bg-green-100 text-green-700"
                      } rounded-lg px-2 py-1`}
                    >
                      {d?.statut?.replace(/_/g, " ").toLowerCase() || "-"}
                    </span>

                    {/* actions only for EN_ATTENTE_DE_TRAITEMENT */}
                    {d?.statut === "EN_ATTENTE_DE_TRAITEMENT" && (
                      <>
                        <button
                          onClick={() => requestValidate(d?.id, true)}
                          className="btn btn-success btn-xs flex items-center gap-1"
                        >
                          <Check size={14} /> Valider
                        </button>
                        <button
                          onClick={() => requestValidate(d?.id, false)}
                          className="btn btn-error btn-xs flex items-center gap-1"
                        >
                          <X size={14} /> Refuser
                        </button>
                      </>
                    )}

                    <button
                      onClick={() => openDetails(d)}
                      className="btn btn-outline btn-xs"
                    >
                      Détails
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-gray-500">
                Aucun abonnement retourné trouvé .
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mb-4">
            <button
              className="btn bg-main text-black btn-sm flex items-center gap-2"
              disabled={
                selectedIds?.length === 0 ||
                batchLoading === "valider" ||
                batchLoading === "rejeter"
              }
              onClick={() =>
                document.getElementById("confirm_action").showModal()
              }
            >
              Valider ({selectedIds?.length})
            </button>
          </div>

          {/* Desktop / xl+: table */}
          <div className="hidden xl:block border rounded-lg overflow-auto">
            <table className="table table-zebra w-full">
              <thead className="bg-main text-black font-mtn">
                <tr>
                  <th className="text-xs px-3 py-3">
                    {ventes?.data?.filter(
                      (e) => e.statut === "EN_ATTENTE_DE_TRAITEMENT",
                    )?.length !== 0 && (
                      <input
                        className="checkbox checkbox-sm"
                        type="checkbox"
                        checked={
                          ventes?.data?.filter(
                            (e) => e.statut === "EN_ATTENTE_DE_TRAITEMENT",
                          )?.length > 0 &&
                          selectedIds?.length ===
                            ventes?.data?.filter(
                              (e) => e.statut === "EN_ATTENTE_DE_TRAITEMENT",
                            )?.length
                        }
                        onChange={handleSelectAll}
                      />
                    )}
                  </th>
                  <th className="text-xs px-3 py-3">Ticket</th>
                  <th className="text-xs px-3 py-3">Client</th>
                  <th className="text-xs px-3 py-3">Téléphone</th>
                  <th className="text-xs px-3 py-3">Commune</th>
                  <th className="text-xs px-3 py-3">Prestataire</th>
                  <th className="text-xs px-3 py-3">Statut</th>
                  <th className="text-xs px-3 py-3">Date création</th>
                  <th className="text-xs px-3 py-3">Superviseur</th>
                  <th className="text-xs px-3 py-3">Motif</th>
                  <th className="text-xs px-3 py-3" style={{ minWidth: 180 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="text-xs">
                {ventes?.data?.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-8">
                      Aucune donnée
                    </td>
                  </tr>
                ) : (
                  ventes?.data?.map((d) => (
                    <tr key={d?.id}>
                      <td className="px-3 py-3">
                        {d?.statut === "EN_ATTENTE_DE_TRAITEMENT" && (
                          <input
                            className="checkbox checkbox-sm "
                            type="checkbox"
                            checked={selectedIds.includes(d.id)}
                            onChange={() => handleSelect(d.id)}
                          />
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {d?.abonnement?.eligibilite?.numeroTicket || d?.id}
                      </td>
                      <td className="px-3 py-3 truncate">
                        {d?.abonnement?.client?.nomPrenoms || "-"}
                      </td>
                      <td className="px-3 py-3">
                        {d?.abonnement?.client?.numero ||
                          d?.abonnement?.numeroMtn ||
                          "-"}
                      </td>
                      <td className="px-3 py-3">
                        {d?.abonnement?.domicile?.quartier?.commune
                          ?.designation || "-"}
                      </td>
                      <td className="px-3 py-3 truncate">
                        {d?.abonnement?.servicePartner?.designation || "-"}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`text-xs inline-block truncate ${
                            d?.statut === "EN_ATTENTE_DE_TRAITEMENT"
                              ? "bg-amber-100 text-amber-700"
                              : d?.statut === "REJETE"
                                ? "bg-red-100 text-red-700"
                                : "bg-green-100 text-green-700"
                          } rounded-lg px-2 py-1`}
                        >
                          {d?.statut?.replace(/_/g, " ").toLowerCase() || "-"}
                        </span>
                      </td>
                      <td className="px-3 py-3 truncate">
                        {d?.dateCreation
                          ? new Date(d?.dateCreation).toLocaleString()
                          : "-"}
                      </td>
                      <td className="px-3 py-3 truncate">
                        {d?.abonnement?.nomSuperviseur || "-"}
                      </td>
                      <td className="px-3 py-3">{d?.motif || "-"}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          {d?.statut === "EN_ATTENTE_DE_TRAITEMENT" && (
                            <>
                              <button
                                onClick={() => requestValidate(d?.id)}
                                className="rounded w-7 h-7 bg-green-100 text-green-600 flex items-center justify-center gap-1"
                              >
                                <Repeat size={14} />
                              </button>
                            </>
                          )}

                          <button
                            onClick={() => openDetails(d)}
                            className="rounded w-7 h-7 bg-gray-300 flex items-center justify-center text-gray-700"
                          >
                            <EyeIcon size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <dialog id="confirm_action" className="modal rounded-none">
            <div className="modal-box w-10/12 max-w-md">
              <div className="modal-action">
                <h1 className="mr-auto text-xl font-bold font-mtn">
                  Modifier le statut
                </h1>
                <form method="dialog">
                  <button className="btn btn-sm">
                    <Cross2Icon />
                  </button>
                </form>
              </div>
              <div className="py-4">
                <p className="text-gray-700">
                  Êtes-vous sûr de vouloir{" "}
                  {actionToConfirm?.valider ? "valider" : "refuser"} ce retour
                  d'abonnement ?
                </p>
              </div>
              <select
                onChange={(e) => setStatusIntention(e.target.value)}
                className="select pl-non w-full"
              >
                <option selected value="">
                  Selectionner un statut
                </option>
                <option value="TRAITE">Traité</option>
                <option value="ABANDON">Abandon</option>
              </select>
              <div>
                <textarea
                  placeholder="commentaire"
                  className="textarea textarea-bordered w-full mt-4"
                  onChange={(e) => setCommentaire(e.target.value)}
                ></textarea>
              </div>

              <div className="modal-action">
                <button onClick={cancelAction} className="btn btn-sm">
                  Annuler
                </button>
                <button
                  onClick={() => handleStatutEdit(statusIntention)}
                  className="btn bg-main border-main btn-sm text-black"
                  disabled={batchLoading}
                >
                  {batchLoading === "valider" && (
                    <Loader2 className="animate-spin h-4 w-4" />
                  )}
                  Enregistrer
                </button>
              </div>
            </div>
          </dialog>

          <dialog id="details_sale" className="modal rounded-none">
            <div className="modal-box w-10/12 max-w-2xl">
              <div className="modal-action">
                <h1 className="mr-auto text-xl font-bold font-mtn">
                  Détails de l'abonnement retourné
                </h1>
                <form method="dialog">
                  <button className="btn btn-sm">
                    <Cross2Icon />
                  </button>
                </form>
              </div>
              <div className="bg-white w-full max-w-3xl flex flex-col">
                <div className="flex-1 overflow-y-auto px-6 py-6">
                  <div className="space-y-8">
                    {/* Client & Abonnement */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Client & Abonnement
                      </h3>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Nom complet
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.client?.nomPrenoms ||
                              "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Téléphone
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.client?.numero ||
                              selectedSale?.abonnement?.numeroMtn ||
                              "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            N° Ticket
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.eligibilite
                              ?.numeroTicket || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Numéro FTTH
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.numeroFtth || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Forfait
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.forfait?.designation ||
                              "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Type Client
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.client?.typeClient ||
                              "-"}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-sm text-gray-600 mb-1">
                            Équipement
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.equipement
                              ?.designation || "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Localisation */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Localisation
                      </h3>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Commune
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.domicile?.quartier
                              ?.commune?.designation || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Quartier
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.domicile?.quartier
                              ?.designation || "-"}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-sm text-gray-600 mb-1">
                            Adresse
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.domicile?.adresse || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">PBO</div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.eligibilite?.pbo?.nom ||
                              "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Type Bâtiment
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.batiment?.designation ||
                              "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Statut & Dates */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Statut & Dates
                      </h3>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Statut retour
                          </div>
                          <span
                            className={`inline-block px-3 py-1.5 rounded-full text-xs font-semibold ${
                              selectedSale?.statut ===
                              "EN_ATTENTE_DE_TRAITEMENT"
                                ? "bg-amber-100 text-amber-700"
                                : selectedSale?.statut === "REJETE"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-green-100 text-green-700"
                            }`}
                          >
                            {selectedSale?.statut
                              ?.replace(/_/g, " ")
                              .toLowerCase() || "-"}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Statut abonnement
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.status?.replace(
                              /_/g,
                              " ",
                            ) || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Date création retour
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.dateCreation
                              ? new Date(
                                  selectedSale.dateCreation,
                                ).toLocaleDateString("fr-FR", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Date création abonnement
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.dateCreation
                              ? new Date(
                                  selectedSale.abonnement.dateCreation,
                                ).toLocaleDateString("fr-FR", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Prestataire & Superviseur */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Service & Superviseur
                      </h3>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Service partenaire
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.servicePartner
                              ?.designation || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Point de service
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.pointService
                              ?.designation || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Superviseur
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.nomSuperviseur || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Contact superviseur
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {selectedSale?.abonnement?.contactSuperviseur ||
                              "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Équipe en charge */}
                    {selectedSale?.equipeEnCharge && (
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">
                          Équipe en charge du retour
                        </h3>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                          <div className="col-span-2">
                            <div className="text-base font-semibold text-gray-900">
                              {selectedSale?.equipeEnCharge?.designation || "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Motif */}
                    {selectedSale?.motif && (
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">
                          Motif du retour
                        </h3>
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {selectedSale.motif}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </dialog>

          <div className="my-4 flex justify-end">
            <Pagination
              count={ventes?.totalPages || 0}
              page={page}
              onChange={(e, v) => loadVentes(v)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AbonnementRetourne;
